import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Search,
  FileText,
  Calendar,
  Edit,
  Trash2,
  Download,
  Award,
  Heart,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Printer,
  BarChart3,
  RefreshCw,
  Loader2,
  Mail,
  Phone,
  MapPin
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const COMPETENCES = [
  "Accueil / Information",
  "Service / Restauration",
  "Logistique / Installation",
  "Sécurité / Prévention",
  "Communication / Réseaux sociaux",
  "Animation / Enfants",
  "Transport / Conduite",
  "Autre"
];

const DISPONIBILITES = [
  "Ponctuelles",
  "Régulières",
  "Week-end",
  "Soirées",
  "En semaine"
];

export default function BenevolesPage() {
  const [benevoles, setBenevoles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterCompetence, setFilterCompetence] = useState("all");
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showContractDialog, setShowContractDialog] = useState(false);
  const [selectedBenevole, setSelectedBenevole] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    date_naissance: "",
    telephone: "",
    email: "",
    adresse: "",
    code_postal: "",
    ville: "",
    competences: [],
    disponibilites: [],
    piece_identite: false,
    assurance: false,
    autre_document: "",
    motivation: "",
    statut: "actif",
    cotisation_payee: false,
    mode_paiement: ""
  });

  const token = localStorage.getItem("token");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [benevolesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/benevoles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/benevoles/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setBenevoles(benevolesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setFormData({
      nom: "",
      prenom: "",
      date_naissance: "",
      telephone: "",
      email: "",
      adresse: "",
      code_postal: "",
      ville: "",
      competences: [],
      disponibilites: [],
      piece_identite: false,
      assurance: false,
      autre_document: "",
      motivation: "",
      statut: "actif",
      cotisation_payee: false,
      mode_paiement: ""
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (showEditDialog && selectedBenevole) {
        await axios.put(
          `${API_URL}/benevoles/${selectedBenevole.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Bénévole mis à jour");
      } else {
        await axios.post(
          `${API_URL}/benevoles`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Bénévole ajouté");
      }
      
      setShowAddDialog(false);
      setShowEditDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce bénévole ?")) return;
    
    try {
      await axios.delete(`${API_URL}/benevoles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bénévole supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const openEditDialog = (benevole) => {
    setSelectedBenevole(benevole);
    setFormData({
      nom: benevole.nom || "",
      prenom: benevole.prenom || "",
      date_naissance: benevole.date_naissance || "",
      telephone: benevole.telephone || "",
      email: benevole.email || "",
      adresse: benevole.adresse || "",
      code_postal: benevole.code_postal || "",
      ville: benevole.ville || "",
      competences: benevole.competences || [],
      disponibilites: benevole.disponibilites || [],
      piece_identite: benevole.piece_identite || false,
      assurance: benevole.assurance || false,
      autre_document: benevole.autre_document || "",
      motivation: benevole.motivation || "",
      statut: benevole.statut || "actif",
      cotisation_payee: benevole.cotisation_payee || false,
      mode_paiement: benevole.mode_paiement || ""
    });
    setShowEditDialog(true);
  };

  const openViewDialog = (benevole) => {
    setSelectedBenevole(benevole);
    setShowViewDialog(true);
  };

  const openContractDialog = (benevole) => {
    setSelectedBenevole(benevole);
    setShowContractDialog(true);
  };

  const handlePrintContract = async (type) => {
    try {
      const response = await axios.get(
        `${API_URL}/benevoles/${selectedBenevole.id}/contrat/${type}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Open print window with HTML content
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${type.replace('_', ' ').toUpperCase()} - ${selectedBenevole.prenom} ${selectedBenevole.nom}</title>
          <style>
            @media print {
              body { margin: 0; padding: 20px; }
            }
          </style>
        </head>
        <body>
          ${response.data.html_content}
          <script>
            setTimeout(() => { window.print(); }, 500);
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
      
    } catch (error) {
      toast.error("Erreur lors de la génération du contrat");
    }
  };

  const toggleCompetence = (comp) => {
    setFormData(prev => ({
      ...prev,
      competences: prev.competences.includes(comp)
        ? prev.competences.filter(c => c !== comp)
        : [...prev.competences, comp]
    }));
  };

  const toggleDisponibilite = (disp) => {
    setFormData(prev => ({
      ...prev,
      disponibilites: prev.disponibilites.includes(disp)
        ? prev.disponibilites.filter(d => d !== disp)
        : [...prev.disponibilites, disp]
    }));
  };

  // Filter benevoles
  const filteredBenevoles = benevoles.filter(b => {
    const matchesSearch = 
      b.nom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.prenom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatut = filterStatut === "all" || b.statut === filterStatut;
    const matchesCompetence = filterCompetence === "all" || b.competences?.includes(filterCompetence);
    
    return matchesSearch && matchesStatut && matchesCompetence;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary flex items-center gap-3">
            <Heart className="w-8 h-8 text-primary" />
            Gestion des Bénévoles
          </h1>
          <p className="text-muted-foreground mt-1">
            Gérez vos bénévoles, leurs compétences et leurs participations
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button onClick={() => { resetForm(); setShowAddDialog(true); }} className="bg-primary">
            <UserPlus className="w-4 h-4 mr-2" />
            Nouveau bénévole
          </Button>
        </div>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Total</p>
                  <p className="text-2xl font-bold text-green-800">{stats.total}</p>
                </div>
                <Users className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Actifs</p>
                  <p className="text-2xl font-bold text-blue-800">{stats.actifs}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600 font-medium">En attente</p>
                  <p className="text-2xl font-bold text-amber-800">{stats.en_attente}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600 font-medium">Cotisation OK</p>
                  <p className="text-2xl font-bold text-emerald-800">{stats.cotisation_payee}</p>
                </div>
                <Award className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Participations</p>
                  <p className="text-2xl font-bold text-purple-800">{stats.participations}</p>
                </div>
                <Calendar className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-rose-600 font-medium">Inactifs</p>
                  <p className="text-2xl font-bold text-rose-800">{stats.inactifs}</p>
                </div>
                <XCircle className="w-8 h-8 text-rose-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Competences Distribution */}
      {stats?.competences_distribution?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Répartition des compétences
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.competences_distribution.map((c, i) => (
                <Badge key={i} variant="outline" className="px-3 py-1">
                  {c.competence}: <span className="font-bold ml-1">{c.count}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher un bénévole..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="actif">Actifs</SelectItem>
                <SelectItem value="inactif">Inactifs</SelectItem>
                <SelectItem value="en_attente">En attente</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterCompetence} onValueChange={setFilterCompetence}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Compétence" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les compétences</SelectItem>
                {COMPETENCES.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Benevoles List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Liste des bénévoles ({filteredBenevoles.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredBenevoles.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun bénévole trouvé</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredBenevoles.map((benevole) => (
                <div
                  key={benevole.id}
                  className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-lg font-bold text-primary">
                          {benevole.prenom?.[0]}{benevole.nom?.[0]}
                        </span>
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-secondary">
                            {benevole.prenom} {benevole.nom}
                          </h3>
                          <Badge 
                            className={
                              benevole.statut === 'actif' ? 'bg-green-100 text-green-800' :
                              benevole.statut === 'inactif' ? 'bg-gray-100 text-gray-800' :
                              'bg-amber-100 text-amber-800'
                            }
                          >
                            {benevole.statut}
                          </Badge>
                          {benevole.cotisation_payee && (
                            <Badge className="bg-emerald-100 text-emerald-800">
                              <Award className="w-3 h-3 mr-1" />
                              Cotisation OK
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {benevole.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {benevole.telephone}
                          </span>
                          {benevole.ville && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {benevole.ville}
                            </span>
                          )}
                        </div>
                        
                        {benevole.competences?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {benevole.competences.slice(0, 4).map((c, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {c}
                              </Badge>
                            ))}
                            {benevole.competences.length > 4 && (
                              <Badge variant="outline" className="text-xs">
                                +{benevole.competences.length - 4}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openViewDialog(benevole)}
                        title="Voir les détails"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openContractDialog(benevole)}
                        title="Contrats"
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openEditDialog(benevole)}
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(benevole.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setShowEditDialog(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              {showEditDialog ? "Modifier le bénévole" : "Nouveau bénévole"}
            </DialogTitle>
            <DialogDescription>
              Remplissez les informations du bénévole
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Info */}
            <div className="space-y-4">
              <h4 className="font-semibold text-secondary flex items-center gap-2">
                <span className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                Informations personnelles
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom *</Label>
                  <Input
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Prénom *</Label>
                  <Input
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Date de naissance</Label>
                  <Input
                    type="date"
                    value={formData.date_naissance}
                    onChange={(e) => setFormData({...formData, date_naissance: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Téléphone *</Label>
                  <Input
                    value={formData.telephone}
                    onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label>Adresse</Label>
                  <Input
                    value={formData.adresse}
                    onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Code postal</Label>
                  <Input
                    value={formData.code_postal}
                    onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Ville</Label>
                  <Input
                    value={formData.ville}
                    onChange={(e) => setFormData({...formData, ville: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Competences */}
            <div className="space-y-4">
              <h4 className="font-semibold text-secondary flex items-center gap-2">
                <span className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                Compétences
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {COMPETENCES.map((comp) => (
                  <label key={comp} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                    <Checkbox
                      checked={formData.competences.includes(comp)}
                      onCheckedChange={() => toggleCompetence(comp)}
                    />
                    <span className="text-sm">{comp}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Disponibilites */}
            <div className="space-y-4">
              <h4 className="font-semibold text-secondary flex items-center gap-2">
                <span className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
                Disponibilités
              </h4>
              <div className="flex flex-wrap gap-2">
                {DISPONIBILITES.map((disp) => (
                  <label key={disp} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50 border">
                    <Checkbox
                      checked={formData.disponibilites.includes(disp)}
                      onCheckedChange={() => toggleDisponibilite(disp)}
                    />
                    <span className="text-sm">{disp}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Documents & Cotisation */}
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-semibold text-secondary flex items-center gap-2">
                  <span className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">4</span>
                  Pièces fournies
                </h4>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.piece_identite}
                      onCheckedChange={(checked) => setFormData({...formData, piece_identite: checked})}
                    />
                    <span>Pièce d'identité</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.assurance}
                      onCheckedChange={(checked) => setFormData({...formData, assurance: checked})}
                    />
                    <span>Assurance</span>
                  </label>
                  <Input
                    placeholder="Autre document..."
                    value={formData.autre_document}
                    onChange={(e) => setFormData({...formData, autre_document: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-semibold text-secondary flex items-center gap-2">
                  <span className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">5</span>
                  Cotisation (20€)
                </h4>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.cotisation_payee}
                      onCheckedChange={(checked) => setFormData({...formData, cotisation_payee: checked})}
                    />
                    <span className="font-medium">Cotisation payée</span>
                  </label>
                  <Select 
                    value={formData.mode_paiement} 
                    onValueChange={(value) => setFormData({...formData, mode_paiement: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Mode de paiement" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="especes">Espèces</SelectItem>
                      <SelectItem value="cheque">Chèque</SelectItem>
                      <SelectItem value="virement">Virement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Motivation & Status */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label>Motivation</Label>
                <Textarea
                  value={formData.motivation}
                  onChange={(e) => setFormData({...formData, motivation: e.target.value})}
                  placeholder="Pourquoi souhaitez-vous rejoindre le collectif ?"
                  rows={3}
                />
              </div>
              <div>
                <Label>Statut</Label>
                <Select 
                  value={formData.statut} 
                  onValueChange={(value) => setFormData({...formData, statut: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="actif">Actif</SelectItem>
                    <SelectItem value="inactif">Inactif</SelectItem>
                    <SelectItem value="en_attente">En attente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowAddDialog(false);
                setShowEditDialog(false);
                resetForm();
              }}>
                Annuler
              </Button>
              <Button type="submit" disabled={submitting} className="bg-primary">
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</>
                ) : (
                  showEditDialog ? "Mettre à jour" : "Ajouter"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary" />
              Détails du bénévole
            </DialogTitle>
          </DialogHeader>
          
          {selectedBenevole && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">
                    {selectedBenevole.prenom?.[0]}{selectedBenevole.nom?.[0]}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-secondary">
                    {selectedBenevole.prenom} {selectedBenevole.nom}
                  </h3>
                  <div className="flex gap-2 mt-1">
                    <Badge className={
                      selectedBenevole.statut === 'actif' ? 'bg-green-100 text-green-800' :
                      selectedBenevole.statut === 'inactif' ? 'bg-gray-100 text-gray-800' :
                      'bg-amber-100 text-amber-800'
                    }>
                      {selectedBenevole.statut}
                    </Badge>
                    {selectedBenevole.cotisation_payee && (
                      <Badge className="bg-emerald-100 text-emerald-800">
                        Cotisation payée
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    {selectedBenevole.email}
                  </p>
                  <p className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="w-4 h-4" />
                    {selectedBenevole.telephone}
                  </p>
                  {selectedBenevole.adresse && (
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      {selectedBenevole.adresse}, {selectedBenevole.code_postal} {selectedBenevole.ville}
                    </p>
                  )}
                </div>
                
                <div>
                  <p className="font-semibold mb-2">Compétences</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedBenevole.competences?.map((c, i) => (
                      <Badge key={i} variant="outline">{c}</Badge>
                    ))}
                  </div>
                </div>
              </div>

              {selectedBenevole.motivation && (
                <div>
                  <p className="font-semibold mb-2">Motivation</p>
                  <p className="text-muted-foreground bg-gray-50 p-3 rounded-lg">
                    {selectedBenevole.motivation}
                  </p>
                </div>
              )}

              {selectedBenevole.participations?.length > 0 && (
                <div>
                  <p className="font-semibold mb-2">Participations récentes</p>
                  <div className="space-y-2">
                    {selectedBenevole.participations.slice(0, 5).map((p, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span>{p.event_titre}</span>
                        <Badge variant="outline">{p.date}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Fermer
            </Button>
            <Button onClick={() => {
              setShowViewDialog(false);
              openContractDialog(selectedBenevole);
            }}>
              <FileText className="w-4 h-4 mr-2" />
              Voir les contrats
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract Dialog */}
      <Dialog open={showContractDialog} onOpenChange={setShowContractDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Documents et contrats
            </DialogTitle>
            <DialogDescription>
              {selectedBenevole && `${selectedBenevole.prenom} ${selectedBenevole.nom}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4"
              onClick={() => handlePrintContract('bulletin_adhesion')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">Bulletin d'Adhésion</p>
                  <p className="text-sm text-muted-foreground">Formulaire d'inscription avec cotisation 20€</p>
                </div>
              </div>
              <Printer className="w-4 h-4 ml-auto" />
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4"
              onClick={() => handlePrintContract('charte')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-green-100 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">Charte du Bénévole</p>
                  <p className="text-sm text-muted-foreground">Engagements réciproques</p>
                </div>
              </div>
              <Printer className="w-4 h-4 ml-auto" />
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4"
              onClick={() => handlePrintContract('reglement')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-amber-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">Règlement Intérieur</p>
                  <p className="text-sm text-muted-foreground">6 articles (Adhésion, Cotisation, etc.)</p>
                </div>
              </div>
              <Printer className="w-4 h-4 ml-auto" />
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4"
              onClick={() => handlePrintContract('fiche_inscription')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">Fiche d'Inscription</p>
                  <p className="text-sm text-muted-foreground">Infos personnelles, compétences, disponibilités</p>
                </div>
              </div>
              <Printer className="w-4 h-4 ml-auto" />
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4"
              onClick={() => handlePrintContract('badge')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-purple-100 flex items-center justify-center">
                  <Award className="w-5 h-5 text-purple-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">Badge Bénévole</p>
                  <p className="text-sm text-muted-foreground">Badge imprimable avec nom</p>
                </div>
              </div>
              <Printer className="w-4 h-4 ml-auto" />
            </Button>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContractDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
